import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { useTabStyles } from 'components/EditUerForm/hooks/useTabStyles';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { FilesSelector } from 'components/FilesSelector/FilesSelector';
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import {
    ControlledCheckbox,
    ControlledSelectField,
    ControlledTabs,
    ControlledTextField,
    ControlNumberField,
} from 'components/Form';
import { ControlledSearchableSelectField } from 'components/Form/ControlledSearchableSelectField';
import { ModalDialog } from 'components/ModalDialog';
import { UerChapter, UerClassifier, workInSumEnumItems } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import type { CatalogRatioGroupData, CatalogUerBaseCostQuery, ConstructionTypeData } from 'gql/graphql';
import { WorkInSumEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { usePreviousValue } from 'hooks/usePrevious';
import { useCatalogWorkTypes } from 'pages/Project/UerProject/WorkOrder/hooks/useCatalogWorkTypes';
import { Grid, GridItem, IconAlert, IconQuestion, Informer } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { AddRatiosModal } from '../Common/AddRatiosModal';
import type { TCheckedIds } from '../Common/AddRatiosModal/types';
import { RatiosFormField } from '../Common/CreateOrEditEstimate/components';

import { useBaseCostRatiosByGroupsData } from './hooks/useBaseCostRatiosByGroupsData';
import { useCatalogBaseCostData } from './hooks/useCatalogBaseCostData';
import { useParseUerCatalog } from './hooks/useParseUerCatalog';
import { useSaveUerEstimate } from './hooks/useSaveUerEstimate';

import Styles from './CreateOrEditUer.module.css';

const workKindQuestionWithTooltip = () => (
    <ComponentWithTooltip
        tooltipText="В случае, если необходимый вид работ отсутствует, его можно создать в справочнике видов работ"
        styles={{ height: 16, marginLeft: 10 }}
        direction="upCenter"
    >
        <IconQuestion view="secondary" size="s" />
    </ComponentWithTooltip>
);

const workRatioWithTooltip = () => (
    <ComponentWithTooltip
        tooltipText="К перехода также доступен для ввода/редактирования на форме НЗ/Изм. к НЗ"
        styles={{ height: 16, marginLeft: 10 }}
        direction="upCenter"
    >
        <IconQuestion view="secondary" size="s" />
    </ComponentWithTooltip>
);

const workRatioRules = {
    validate: (value: any, formData: { workInSum: { id: WorkInSumEnum } }) => {
        if (!value) {
            if (![WorkInSumEnum.ParticipateWithRatio, WorkInSumEnum.Participate].includes(formData?.workInSum?.id)) {
                return true;
            }

            return 'Поле обязательно для заполнения';
        }

        return true;
    },
};

const uerChaptersItems = Object.entries(UerChapter).map(([key, value]) => ({ id: key, label: value }));

const uerClassifierItems = Object.entries(UerClassifier).map(([key, value]) => ({ id: key, label: value }));

interface ICreateOrEditUerProps {
    onClose: () => void;
    isEdit?: boolean;
    constructionTypes?: ConstructionTypeData[] | null;
    uerBaseCostId: number;
    catalogId: number | undefined;
    catalogUniqueId: string | undefined;
    refreshData: () => void;
    isUERBaseCostWork: boolean;
    catalogVersion?: string;
    catalogName: string;
}

export const CreateOrEditEstimate: React.FC<ICreateOrEditUerProps> = (props) => {
    const {
        onClose,
        isEdit = false,
        constructionTypes,
        uerBaseCostId,
        catalogId,
        refreshData,
        isUERBaseCostWork,
        catalogVersion,
        catalogName,
        catalogUniqueId,
    } = props;

    const checkboxConstructionTypes = constructionTypes?.map(({ id }) => `checkbox_constructionTypes_${id}`);

    const fieldsToCheck = {
        0: ['uerClassifier', 'chapter', 'justification'],
        1: checkboxConstructionTypes || [],
        2: ['workRatio', 'workInSum', 'workKind'],
    };

    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [isAddCoefficientModalOpen, setIsAddCoefficientModalOpen] = useState(false);

    const modalTitle = useMemo(() => (isEdit ? 'редактирование расценки' : 'Создание расценки'), [isEdit]);
    const saveButtonTitle = useMemo(() => (isEdit ? 'Сохранить' : 'Создать'), [isEdit]);

    const base64Files = useReadFilesAsBase64(selectedFile);

    const dispatch = useDispatch();

    const { control, handleSubmit, getValues, setValue, watch, reset, clearErrors, formState } = useForm();

    const watchForm = watch();

    const checkedIdCheckboxes = Object.entries(watchForm)
        .filter(([key, value]) => key.startsWith('checkbox_constructionTypes') && value === true)
        .map(([key]) => Number(key.replace('checkbox_constructionTypes_', '')));

    const importedRatioGroupIds = useMemo(
        () => (watchForm?.ratioGroups || []).map((ratioGroup: CatalogRatioGroupData) => ratioGroup.id),
        [watchForm?.ratioGroups],
    );

    const items = useMemo(
        () => [
            { id: 0, label: 'Общие данные' },
            { id: 1, label: 'Виды строительства', count: checkedIdCheckboxes.length },
            ...(!isUERBaseCostWork ? [{ id: 2, label: 'Вид работ' }] : []),
        ],
        [checkedIdCheckboxes.length, isUERBaseCostWork],
    );

    const [activeTab, setActiveTab] = useState(items[0]);

    const onSuccessGetData = useCallback(
        (
            depsVars: TUseRequestUpdaterProps<
                CatalogUerBaseCostQuery,
                {
                    catalogUniqueId: string;
                    uerBaseCostId: number;
                }
            >,
        ) => {
            const {
                chapter,
                sectionName,
                groupName,
                code,
                name,
                unit,
                unitCost,
                uerClassifier,
                workType,
                constructionTypes: rowDataConstructionTypes,
                ratios,
                ratioGroups,
                justification,
            } = depsVars?.payload?.catalogUERBaseCost?.data ?? {};

            if (rowDataConstructionTypes?.length) {
                const defaultCheckboxValues = rowDataConstructionTypes.reduce<Record<string, boolean>>((acc, item) => {
                    acc[item.id.toString()] = true;

                    return acc;
                }, {});

                Object.entries(defaultCheckboxValues).forEach(([key, value]) => {
                    setValue(`checkbox_constructionTypes_${key}`, value);
                });
            }

            setValue('chapter', { label: chapter ? UerChapter[chapter] : '', id: chapter });
            setValue('sectionName', sectionName);
            setValue('groupName', groupName);
            setValue('code', code);
            setValue('name', name);
            setValue('unit', unit);
            setValue('unitCost', unitCost);
            setValue('justification', justification);
            setValue('uerClassifier', { label: uerClassifier ? UerClassifier[uerClassifier] : '', id: uerClassifier });
            if (workType) {
                const workInSumEnumCurrentItems = workInSumEnumItems?.filter((i) => i.id === workType.workInSum);

                setValue('workKind', { label: workType.workName, id: workType.workUniqueId });

                setValue('workCode', workType.workCode);
                setValue('workUnit', workType.workUnit);
                setValue('workInSum', workInSumEnumCurrentItems[0]);
                setValue('workRatio', workType.workRatio);
            }
            setValue('ratioGroups', ratioGroups);
            setValue('precheckedRatios', ratios);
            if (ratios?.length) {
                const precheckedRatioGroups = ratios.reduce((acc, ratio) => {
                    if (acc[ratio.ratioType]) {
                        acc[ratio.ratioType][ratio.id] = ratio;
                    }

                    return acc;
                }, {} as TCheckedIds);

                setValue('ratioGroupsComposedFromRatios', precheckedRatioGroups);
            }
        },
        [setValue],
    );

    const { data: rowData } = useCatalogBaseCostData({
        uerBaseCostId,
        catalogUniqueId,
        withEffect: isEdit,
        onSuccess: onSuccessGetData,
    });

    const { data: ratiosFromGroups } = useBaseCostRatiosByGroupsData({
        ratioGroups: importedRatioGroupIds,
    });

    const {
        parsedUerData,
        uerDataParseRequestSuccess,
        setUerDataParseRequestSuccess,
        parsedUerStatus,
        parsedUerRequest,
        resetParsedDataHandler,
        onCloseParseErrorModal,
        parsedUerError,
        wrongParseUerParameters,
        wrongParseUerParametersData,
        wrongParseUerParametersCreateReport,
    } = useParseUerCatalog({
        reset,
        setValue,
        getValues,
        rowData,
        isEdit,
        catalogVersion,
        catalogName,
    });

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    useEffect(() => {
        if (base64Files?.length) {
            dispatch(
                parsedUerRequest({
                    content: base64Files[0],
                }),
            );
        }
    }, [base64Files, dispatch, getValues, parsedUerRequest]);

    const { catalogWorkTypes, catalogWorkTypesRequest, onNextPageRequest, catalogWorkTypesStatus, hasNextPage } =
        useCatalogWorkTypes();

    const workKindItems = useMemo(
        () =>
            catalogWorkTypes.map((item) => ({
                id: item.uniqueId,
                label: item.name,
                workCode: item.code,
                workUnit: item.unit,
            })),
        [catalogWorkTypes],
    );

    const tabStyles = useTabStyles({ tabItems: items, activeTab });

    const onUploadUerFile = useCallback(
        (files) => {
            if (files.length) {
                setUerDataParseRequestSuccess(false);
                setSelectedFile(files);
            } else if (!files.length && selectedFile?.length) {
                setSelectedFile([]);
                resetParsedDataHandler(undefined);
            }
        },
        [resetParsedDataHandler, selectedFile?.length, setUerDataParseRequestSuccess],
    );

    const isFieldDisabled = !uerDataParseRequestSuccess;

    const currentWorkKindItem = useWatch({ control, name: 'workKind' });
    const prevWorkKindItem = usePreviousValue(currentWorkKindItem);
    const currentWorkInSum = useWatch({ control, name: 'workInSum' });
    const precheckedRatios = useWatch({ control, name: 'precheckedRatios' });
    const ratios = useWatch({ control, name: 'ratios' });

    useEffect(() => {
        const safePrecheckedItems = Array.isArray(precheckedRatios)
            ? [...precheckedRatios, ...ratiosFromGroups]
            : ratiosFromGroups;

        setValue('ratios', R.pipe(R.uniqBy(R.prop('id')), R.sortBy(R.prop('code')))(safePrecheckedItems));
    }, [precheckedRatios, ratiosFromGroups, setValue]);

    const isWorkRatioEnabled = useMemo(
        () =>
            [WorkInSumEnum.ParticipateWithRatio, WorkInSumEnum.Participate].includes(
                currentWorkInSum?.id || rowData?.workType?.workInSum,
            ),
        [currentWorkInSum?.id, rowData?.workType?.workInSum],
    );

    useEffect(() => {
        if (!isWorkRatioEnabled) {
            setValue('workRatio', null);
        }
    }, [isWorkRatioEnabled, setValue]);

    useEffect(() => {
        const isManualChangedWorkKind = currentWorkKindItem && currentWorkKindItem?.id !== prevWorkKindItem?.id;

        if (isManualChangedWorkKind) {
            setValue('workCode', currentWorkKindItem.workCode);
            setValue('workUnit', currentWorkKindItem.workUnit);
            setValue('workInSum', null);
            setValue('workRatio', null);
        }
    }, [currentWorkKindItem, prevWorkKindItem, setValue]);

    const resetModalDataSaveData = useCallback(() => {
        setSelectedFile(null);
        resetParsedDataHandler(undefined);
        reset({});
    }, [reset, resetParsedDataHandler]);

    const { onSubmit, saveStatus, saveDataReset, isSubmittedErr, dataRequestErr, currentError, handleCloseModalError } =
        useSaveUerEstimate({
            onClose,
            watchForm,
            checkedIdCheckboxes,
            catalogId,
            parsedUerData: parsedUerData?.data,
            resetModalDataSaveData,
            refreshData,
            isEdit,
            rowData,
        });

    useEffect(
        () => () => {
            saveDataReset(undefined);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const isFormChanged = useMemo(() => formState.isDirty || !isFieldDisabled, [formState.isDirty, isFieldDisabled]);

    const actionModalClose = useCallback(() => {
        resetModalDataSaveData();
        saveDataReset(undefined);
        onClose();
    }, [onClose, resetModalDataSaveData, saveDataReset]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: actionModalClose,
        });

    useEffect(() => {
        if (checkedIdCheckboxes.length !== 0) {
            clearErrors(checkboxConstructionTypes);
        }
    }, [checkboxConstructionTypes, checkedIdCheckboxes.length, clearErrors]);

    const oncloseModal = useCallback(() => {
        if (isFormChanged) {
            handleOpenConfirmationDialog();
        } else {
            actionModalClose();
        }
    }, [actionModalClose, handleOpenConfirmationDialog, isFormChanged]);

    const openAddCoefficientModal = useCallback(() => {
        setIsAddCoefficientModalOpen(true);
    }, []);

    const closeAddCoefficientModal = useCallback(() => {
        setIsAddCoefficientModalOpen(false);
    }, []);

    const onAddRatios = useCallback(
        (checkedRatioGroups) => {
            const checkedRatiosFlat = R.sortBy(R.prop('code'))(
                R.values(checkedRatioGroups)?.reduce((acc, group) => [...acc, ...R.values(group)], []),
            );

            setValue('ratios', checkedRatiosFlat);
        },
        [setValue],
    );

    return (
        <ModalDialog
            isOpen
            title={modalTitle}
            onConfirm={handleSubmit(onSubmit)}
            onClose={oncloseModal}
            withTitleCloseButton
            btnConfirmLabel={saveButtonTitle}
            style={{ width: '600px' }}
            isOnClickOutsideDisable
            btnConfirmDisabled={isEdit ? undefined : !uerDataParseRequestSuccess}
            isCommit={saveStatus === RequestStatus.FETCHING || parsedUerStatus === RequestStatus.FETCHING}
        >
            <div className={Styles.modalBody}>
                {!isEdit && (
                    <Informer
                        size="s"
                        status="warning"
                        icon={IconAlert}
                        label="Новая расценка будет добавлена в Cправочник расценок"
                        className={Styles.informer}
                    />
                )}
                <div className={`${Styles.heightFilesSelector} ${selectedFile?.length ? Styles.selectedFile : ''}`}>
                    <FilesSelector
                        accept={['.xml']}
                        isLoading={false}
                        manualOperationName="загрузите"
                        onFilesChange={onUploadUerFile}
                        isExternalFilesState
                        files={selectedFile}
                        customNameFile="сметы-основания"
                    />
                </div>
                <ControlledTabs
                    value={activeTab}
                    onChange={onTabChange}
                    items={items}
                    view="bordered"
                    size="s"
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                    transparentTag
                />
                <div className={Styles.tabsBody}>
                    <Grid rowGap="s" cols="1" style={tabStyles[0]}>
                        <GridItem>
                            <ControlledTextField
                                name="sectionName"
                                label="Раздел"
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name="groupName"
                                label="Подраздел"
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField name="code" label="Шифр" control={control} type="text" disabled />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name="name"
                                label="Наименование"
                                control={control}
                                type="text"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField name="unit" label="Ед. изм." control={control} type="text" disabled />
                        </GridItem>
                        <GridItem>
                            <ControlNumberField
                                name="unitCost"
                                label={
                                    (
                                        <span>
                                            Стоимость единицы УЕР<div>в базисном уровне цен 2001 года, руб.без НДС</div>
                                        </span>
                                    ) as any
                                }
                                labelPosition="left"
                                control={control}
                                disabled
                                size="s"
                                className={Styles.basePriceField}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledSelectField
                                name="uerClassifier"
                                label="Тип УЕР"
                                control={control}
                                items={uerClassifierItems}
                                disabled={isEdit ? undefined : isFieldDisabled}
                                rules={requiredBaseRule}
                                isTooltipHidden={!!tabStyles[0]}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledSelectField
                                name="chapter"
                                label="Глава в расчете"
                                control={control}
                                items={uerChaptersItems}
                                rules={requiredBaseRule}
                                disabled={isEdit ? undefined : isFieldDisabled}
                                isTooltipHidden={!!tabStyles[0]}
                            />
                        </GridItem>
                        <GridItem>
                            <RatiosFormField
                                control={control}
                                items={ratios ?? []}
                                onOpenAddCoefficientModal={openAddCoefficientModal}
                                disabled={isEdit ? undefined : isFieldDisabled}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name="justification"
                                label="Обоснование"
                                control={control}
                                type="textarea"
                                disabled={isEdit ? undefined : isFieldDisabled}
                                placeholder={!isEdit && isFieldDisabled ? '' : undefined}
                                rules={{
                                    ...requiredBaseRule,
                                    maxLength: {
                                        value: 1024,
                                        message: 'не более 1024 символов',
                                    },
                                }}
                            />
                        </GridItem>
                    </Grid>
                    <Grid rowGap="s" cols="1" style={tabStyles[1]}>
                        {constructionTypes?.map(({ id, name }: any) => (
                            <GridItem key={id}>
                                <div className={Styles.checkboxRow}>
                                    <ControlledCheckbox
                                        key={id}
                                        name={`checkbox_constructionTypes_${id}`}
                                        label={name}
                                        control={control}
                                        disabled={isFieldDisabled && !isEdit}
                                        required={checkedIdCheckboxes.length === 0}
                                    />
                                </div>
                            </GridItem>
                        ))}
                    </Grid>
                    {!isUERBaseCostWork && (
                        <Grid rowGap="s" cols="1" style={tabStyles[2]}>
                            <GridItem>
                                <ControlledSearchableSelectField
                                    name="workKind"
                                    label="Вид работ"
                                    control={control}
                                    items={workKindItems}
                                    labelIcon={workKindQuestionWithTooltip}
                                    className="labelWithEndingIcon"
                                    disabled={isFieldDisabled && !isEdit}
                                    rules={requiredBaseRule}
                                    labelPosition="left"
                                    isTooltipHidden={!!tabStyles[2]}
                                    direction="rightCenter"
                                    onSearch={catalogWorkTypesRequest}
                                    onNextPageRequest={onNextPageRequest}
                                    isLoading={catalogWorkTypesStatus === RequestStatus.FETCHING}
                                    hasNextPage={hasNextPage}
                                    isLazyLoadEnabled
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="workCode"
                                    label="Шифр"
                                    control={control}
                                    type="text"
                                    disabled
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledTextField
                                    name="workUnit"
                                    label="Ед. изм."
                                    control={control}
                                    type="text"
                                    disabled
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledSelectField
                                    name="workInSum"
                                    label="Участие в суммировании ФО по виду работ"
                                    control={control}
                                    items={workInSumEnumItems}
                                    disabled={isFieldDisabled && !isEdit}
                                    rules={requiredBaseRule}
                                    isTooltipHidden={!!tabStyles[2]}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlNumberField
                                    name="workRatio"
                                    label="К перехода"
                                    control={control}
                                    type="number"
                                    size="s"
                                    decimalScale={4}
                                    labelPosition="left"
                                    className="labelWithEndingIcon"
                                    width="full"
                                    disabled={(isFieldDisabled && !isEdit) || !isWorkRatioEnabled}
                                    rules={workRatioRules as any}
                                    labelIcon={workRatioWithTooltip}
                                    isTooltipHidden={!!tabStyles[2]}
                                />
                            </GridItem>
                        </Grid>
                    )}
                </div>
            </div>

            {isAddCoefficientModalOpen && (
                <AddRatiosModal
                    closeModal={closeAddCoefficientModal}
                    onAdd={onAddRatios}
                    precheckedRatios={ratios ?? []}
                />
            )}

            <ErrorHandlingModal
                titleText="Загрузка Сметы"
                isSubmitted={wrongParseUerParameters || uerDataParseRequestSuccess}
                statusRequest={parsedUerStatus}
                dataRequest={wrongParseUerParametersData || parsedUerData}
                errorsRequest={parsedUerError}
                handleCloseModalError={onCloseParseErrorModal}
                handleCreateReport={wrongParseUerParametersCreateReport}
            />
            <ErrorHandlingModal
                titleText={isEdit ? 'Редактирование расценки' : 'Создание расценки'}
                isSubmitted={isSubmittedErr}
                statusRequest={saveStatus}
                dataRequest={dataRequestErr}
                errorsRequest={currentError}
                handleCloseModalError={handleCloseModalError}
            />
            <ConfirmationDialog
                textHeader="Отмена действий"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

CreateOrEditEstimate.displayName = 'CreateOrEditEstimate';
